import { takeLatest, put, all, select, delay } from 'redux-saga/effects';
import {
  GENERAL_SEARCH,
  CLEAR_GENERAL_SEARCH,
  SEARCH_ORDERS_SUCCESS,
  SEARCH_CLIENTS_SUCCESS,
  SEARCH_MESSAGES_SUCCESS,
  ORDERS_SEARCH,
  CLIENTS_SEARCH,
  SELECT_SEARCH_ORDER,
  SELECT_SEARCH_CLIENT,
  SEARCH_COMMUNICATIONS_SUCCESS,
  SEARCH_NOTES_SUCCESS,
  COMMUNICATIONS_SEARCH,
  NOTES_SEARCH,
  SELECT_SEARCH_COMMUNICATION,
  SELECT_SEARCH_NOTE,
  SELECT_SEARCH_MESSAGE,
  MESSAGES_SEARCH,
} from '../actions/search';
import { safeApply, safeCall } from './errors';
import fetchData from '../../api/fetchData';
import { fetchPerson } from './persons';
import { getQueryWithPhones } from './utils';
import {
  SELECT_CLIENT,
  SELECT_TICKET,
  SET_TICKET_FILTERS,
  SET_CLIENTS_OPTIONS,
} from '../actions/tickets';
import { FETCH_CHAIN } from '../actions/chains';
import { ROUTE } from '../../consts/pages';
import {
  serializeSearchOrders,
  serializeSearchMails,
  serializeSearchPersons,
  serializeSearchNotes,
  serializeSearchMessages,
} from '../serializers/search';

const apiOrdersUrl = `${process.env.API_HOST_URL}${process.env.API_ORDERS}`;
const apiPersonsUrl = `${process.env.API_HOST_URL}${process.env.API_PERSONS}`;
const apiMailsUrl = `${process.env.API_HOST_URL}${process.env.API_MAILS}`;
const apiMessagesUrl = `${process.env.API_HOST_URL}${process.env.API_MESSAGES}`;
const apiTicketLogsUrl = `${process.env.API_URL}/ticket_logs`;

const SEARCH_PAGE_SIZE = 20;

export function* fetchSearchOrders({
  query,
  page = 1,
  pageSize = SEARCH_PAGE_SIZE,
}) {
  return yield safeCall(
    fetchData,
    `${apiOrdersUrl}/search?q=${encodeURIComponent(
      query
    )}&size=${pageSize}&page=${page}&persit=true`,
    {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    }
  );
}

export function* fetchSearchPersons({
  query,
  page = 1,
  pageSize = SEARCH_PAGE_SIZE,
  type = 'client',
}) {
  return yield safeCall(
    fetchData,
    `${apiPersonsUrl}/search?q=${encodeURIComponent(
      query
    )}&size=${pageSize}&page=${page}&type=${type}`,
    {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    }
  );
}

export function* fetchSearchCommunications({
  query,
  page = 1,
  pageSize = SEARCH_PAGE_SIZE,
}) {
  return yield safeCall(
    fetchData,
    `${apiMailsUrl}/search?q=${encodeURIComponent(
      query
    )}&size=${pageSize}&page=${page}`,
    {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    }
  );
}

export function* fetchSearchMessages({
  query,
  page = 1,
  pageSize = SEARCH_PAGE_SIZE,
}) {
  return yield safeCall(
    fetchData,
    `${apiMessagesUrl}/search?q=${encodeURIComponent(
      query
    )}&size=${pageSize}&page=${page}`,
    {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    }
  );
}

export function* fetchSearchNotes({
  query,
  page = 1,
  pageSize = SEARCH_PAGE_SIZE,
}) {
  return yield safeCall(
    fetchData,
    `${apiTicketLogsUrl}/search?q=${encodeURIComponent(
      query
    )}&size=${pageSize}&page=${page}`,
    {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    }
  );
}

export function* fetchSearchSuggests({ query, pageSize = SEARCH_PAGE_SIZE }) {
  return yield safeCall(
    fetchData,
    `${apiPersonsUrl}/search_ranged?q=${encodeURIComponent(
      query
    )}&size=${pageSize}&page=${1}`,
    {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    }
  );
}

export function* mainSearchOrders({
  payload: { query, pageSize = SEARCH_PAGE_SIZE },
}) {
  const page = yield select(({ search }) => search.orderPage);
  const data = yield safeCall(fetchSearchOrders, { query, page, pageSize });
  const { orders, meta } = serializeSearchOrders(data);

  yield put({ payload: { orders, meta }, type: SEARCH_ORDERS_SUCCESS });
}

export function* mainSearchPersons({
  payload: { query, pageSize = SEARCH_PAGE_SIZE },
}) {
  const page = yield select(({ search }) => search.clientPage);
  const data = yield safeCall(fetchSearchPersons, { query, page, pageSize });

  const { persons, meta } = serializeSearchPersons(data);

  yield put({ payload: { persons, meta }, type: SEARCH_CLIENTS_SUCCESS });
}

export function* mainSearchCommunication({
  payload: { query, pageSize = SEARCH_PAGE_SIZE },
}) {
  const page = yield select(({ search }) => search.communicationPage);
  const data = yield safeCall(fetchSearchCommunications, {
    page,
    query,
    pageSize,
  });
  const { mails, meta } = serializeSearchMails(data);

  yield put({ payload: { mails, meta }, type: SEARCH_COMMUNICATIONS_SUCCESS });
}

export function* mainSearchMessages({
  payload: { query, pageSize = SEARCH_PAGE_SIZE },
}) {
  return; // TODO Выключили в рамках задачи LT-46118, вернуть как будет готов бэк
  const page = yield select(({ search }) => search.clientPage);
  const data = yield safeCall(fetchSearchMessages, { query, page, pageSize });

  const { messages, meta } = serializeSearchMessages(data);

  yield put({ payload: { messages, meta }, type: SEARCH_MESSAGES_SUCCESS });
}

export function* mainSearchNotes({
  payload: { query, pageSize = SEARCH_PAGE_SIZE },
}) {
  const page = yield select(({ search }) => search.notePage);
  const data = yield safeCall(fetchSearchNotes, { query, page, pageSize });
  const { notes, meta } = serializeSearchNotes(data);

  yield put({ payload: { notes, meta }, type: SEARCH_NOTES_SUCCESS });
}

export function* generalSearch({
  payload: { query: unhandledQuery, pageSize },
}) {
  if (unhandledQuery.length < 3) {
    yield put({ type: CLEAR_GENERAL_SEARCH });
    return;
  }

  const query = getQueryWithPhones(unhandledQuery);
  yield delay(1500);
  yield all([
    safeCall(mainSearchOrders, { payload: { query, pageSize } }),
    safeCall(mainSearchPersons, { payload: { query, pageSize } }),
    safeCall(mainSearchCommunication, { payload: { query, pageSize } }),
    safeCall(mainSearchNotes, { payload: { query, pageSize } }),
    safeCall(mainSearchMessages, { payload: { query, pageSize } }),
  ]);
}

export function* selectSearchOrder({ payload: { clientId, history } }) {
  history.push(ROUTE.TICKETS);
  const person = yield safeCall(fetchPerson, { id: clientId, type: 'client' });
  yield put({
    payload: { clientId, history },
    type: SELECT_CLIENT,
  });
  yield put({
    payload: [{ value: clientId, name: person.fullName }],
    type: SET_CLIENTS_OPTIONS,
  });
  yield put({
    payload: { filters: { client_id: clientId } },
    type: SET_TICKET_FILTERS,
  });
}

export function* selectSearchClient({ payload: { clientId, history } }) {
  history.push(ROUTE.TICKETS);
  const person = yield safeCall(fetchPerson, { id: clientId, type: 'client' });
  yield put({
    payload: { clientId, history },
    type: SELECT_CLIENT,
  });
  yield put({
    payload: [{ value: clientId, name: person.fullName }],
    type: SET_CLIENTS_OPTIONS,
  });
}

export function* selectSearchCommunication({
  payload: { ticketId, chainId, history },
}) {
  history.push(ROUTE.TICKETS);
  yield put({ type: SELECT_TICKET, payload: { history, id: ticketId } });
  yield put({ type: FETCH_CHAIN, payload: { history, id: chainId } });
}

export function* selectSearchMessage({
  payload: { ticketId, chainId, history },
}) {
  history.push(ROUTE.TICKETS);
  yield put({ type: SELECT_TICKET, payload: { history, id: ticketId } });
  yield put({ type: FETCH_CHAIN, payload: { history, id: chainId } });
}

export function* selectSearchNote({ payload: { ticketId, history } }) {
  history.push(ROUTE.TICKETS);
  yield put({ type: SELECT_TICKET, payload: { history, id: ticketId } });
}

export default function* () {
  yield all([
    yield takeLatest(ORDERS_SEARCH, safeApply(mainSearchOrders)),
    yield takeLatest(CLIENTS_SEARCH, safeApply(mainSearchPersons)),
    yield takeLatest(COMMUNICATIONS_SEARCH, safeApply(mainSearchCommunication)),
    yield takeLatest(NOTES_SEARCH, safeApply(mainSearchNotes)),
    yield takeLatest(MESSAGES_SEARCH, safeApply(mainSearchMessages)),
    yield takeLatest(SELECT_SEARCH_ORDER, safeApply(selectSearchOrder)),
    yield takeLatest(SELECT_SEARCH_CLIENT, safeApply(selectSearchClient)),
    yield takeLatest(
      SELECT_SEARCH_COMMUNICATION,
      safeApply(selectSearchCommunication)
    ),
    yield takeLatest(SELECT_SEARCH_MESSAGE, safeApply(selectSearchMessage)),
    yield takeLatest(SELECT_SEARCH_NOTE, safeApply(selectSearchNote)),
    yield takeLatest(GENERAL_SEARCH, safeApply(generalSearch)),
  ]);
}
